import { addEventListener } from "@oddcamp/js-utils/src/event"
import { addClass, removeClass } from "@oddcamp/js-utils/src/attribute"
import { createFocusTrap } from "focus-trap"

import init from "../../shared/utils/turbo-init"

init(() => {
  const filterEl = document.querySelector(`.js--mission-search-filter`)
  const openButtonsSelector = `.js--mission-search-filter-open, .js--mission-search-location-filter-open`
  const getOpenButtons = () => document.querySelectorAll(openButtonsSelector)
  if (!filterEl) return undefined

  const backdropEl = filterEl.querySelector(
    `.js--mission-search-filter-backdrop`
  )
  const closeButton = filterEl.querySelector(`.js--mission-search-filter-close`)

  const panelEl = filterEl.querySelector(`.mission-search-filter__panel`)

  const focusTrap = createFocusTrap(panelEl, {
    escapeDeactivates: true,
    clickOutsideDeactivates: true,
    allowOutsideClick: true,
    onDeactivate() {
      close()
    },
  })

  const open = (element) => {
    addClass(filterEl, `is-open`)
    filterEl.setAttribute(`aria-hidden`, `false`)
    focusTrap.activate()

    if (element.classList.contains(`js--mission-search-location-filter-open`)) {
      document
        .querySelector(
          `[data-section="location"] button.js--mission-filter-section-toggle`
        )
        .click()
    }
  }

  const close = () => {
    removeClass(filterEl, `is-open`)
    filterEl.setAttribute(`aria-hidden`, `true`)
    focusTrap.deactivate()
  }

  const onDocumentClick = (event) => {
    const trigger = event.target.closest(openButtonsSelector)
    if (!trigger) return
    open(trigger)
  }
  document.addEventListener(`click`, onDocumentClick)

  if (backdropEl) {
    addEventListener(backdropEl, `click`, close)
  }

  if (closeButton) {
    addEventListener(closeButton, `click`, close)
  }

  // Accordion behaviour for sections inside the filter
  const sectionEls = filterEl.querySelectorAll(`.js--mission-filter-section`)
  const updateSectionSummary = (sectionEl) => {
    const summaryEl = sectionEl.querySelector(
      `.js--mission-filter-section-summary .vb-badge`
    )
    if (!summaryEl) return

    // Generic fallback: count checked inputs in section
    const inputs = sectionEl.querySelectorAll(
      `input[type="checkbox"], input[type="radio"]`
    )
    const checkedCount = Array.prototype.filter.call(
      inputs,
      (input) => input.checked
    ).length

    summaryEl.textContent = checkedCount ? `${checkedCount}` : ``
  }

  const updateAllSummaries = () => {
    sectionEls.forEach((section) => updateSectionSummary(section))
  }

  // Count active sections (not individual cities/values). A section counts as 1 if it has any checked input
  const getActiveSectionCount = () => {
    let active = 0
    sectionEls.forEach((section) => {
      const inputs = section.querySelectorAll(
        `input[type="checkbox"], input[type="radio"]`
      )
      const anyChecked = Array.prototype.some.call(inputs, (i) => i.checked)
      if (anyChecked) active += 1
    })
    return active
  }

  // Update the badge on the external "Filter" open button(s) with number of active sections
  const updateOpenButtonBadge = () => {
    const count = getActiveSectionCount()
    getOpenButtons().forEach((btn) => {
      const badge = btn.querySelector(`.vb-badge`)
      if (!badge) return
      badge.textContent = count > 0 ? String(count) : ``
    })
  }

  // Global clear label: show active filters count, e.g. "Rensa alla filter (3)"
  const clearAllLabelEl = filterEl.querySelector(
    `.mission-search-filter__clear-all-label`
  )

  const clearAllLinkEl = filterEl.querySelector(
    `.mission-search-filter__clear-all`
  )

  const clearFiltersBtnEl = document.querySelector(
    `.js--mission-search-filter-clear`
  )

  const getActiveFilterCount = () => {
    const inputs = filterEl.querySelectorAll(
      `.mission-search-filter__fields input[type="checkbox"], .mission-search-filter__fields input[type="radio"]`
    )
    return Array.prototype.filter.call(inputs, (i) => i.checked).length
  }

  const updateClearAllVisibility = () => {
    const hasActive = getActiveFilterCount() > 0
    if (clearAllLabelEl) clearAllLabelEl.hidden = !hasActive
    if (clearAllLinkEl) clearAllLinkEl.hidden = !hasActive
    if (clearFiltersBtnEl) {
      clearFiltersBtnEl.style.display = hasActive ? `flex` : `none`
    }
  }

  const updateClearAllLabelCount = () => {
    if (!clearAllLabelEl) return
    const count = getActiveFilterCount()
    if (count > 0) {
      clearAllLabelEl.querySelector(`.vb-badge`).textContent = `${count}`
    } else {
      clearAllLabelEl.querySelector(`.vb-badge`).textContent = ``
    }
  }

  const updateLocationActiveStates = () => {
    const locationCheckboxes = filterEl.querySelectorAll(
      `.js--mission-filter-location`
    )

    locationCheckboxes.forEach((checkbox) => {
      const rowEl = checkbox.closest(`.mission-search-filter__location`)
      if (!rowEl) return

      if (checkbox.checked) {
        rowEl.classList.add(`is-active`)
      } else {
        rowEl.classList.remove(`is-active`)
      }
    })

    // Update county-level clear button visibility and count
    const countyEls = filterEl.querySelectorAll(`.js--mission-filter-county`)
    countyEls.forEach((countyEl) => {
      const checkboxes = countyEl.querySelectorAll(
        `.js--mission-filter-location`
      )
      const activeCount = Array.prototype.filter.call(
        checkboxes,
        (cb) => cb.checked
      ).length

      const clearBtn = countyEl.querySelector(
        `.js--mission-filter-county-clear`
      )
      const countEl = countyEl.querySelector(`.js--mission-filter-county-count`)
      const countyAccordionBtnEl = countyEl.querySelector(
        `.js--mission-filter-county-toggle`
      )

      if (activeCount > 0) {
        countyAccordionBtnEl.classList.add(`is-active`)
      } else {
        countyAccordionBtnEl.classList.remove(`is-active`)
      }

      if (countEl) countEl.textContent = String(activeCount)
      if (clearBtn) {
        if (activeCount > 0) {
          clearBtn.hidden = false
        } else {
          clearBtn.hidden = true
        }
      }
    })
  }

  // Use event delegation on the document so the toggles keep working even if
  // Turbo (or anything else) replaces the filter panel DOM after the initial
  // page load. Direct per-button listeners would be lost in that case because
  // turbo:load does not fire on frame-only updates.
  const onSectionToggleClick = (event) => {
    const headerButton = event.target.closest(
      `.js--mission-filter-section-toggle`
    )
    if (!headerButton) return
    const sectionEl = headerButton.closest(`.js--mission-filter-section`)
    if (!sectionEl || !filterEl.contains(sectionEl)) return
    const bodyEl = sectionEl.querySelector(
      `.mission-search-filter__section-body`
    )
    if (!bodyEl) return

    const expanded = headerButton.getAttribute(`aria-expanded`) === `true`
    headerButton.setAttribute(`aria-expanded`, expanded ? `false` : `true`)
    bodyEl.hidden = expanded
    sectionEl.classList.toggle(`is-open`, !expanded)
  }
  document.addEventListener(`click`, onSectionToggleClick)

  // County-level clear buttons for Plats
  const countyClearButtons = filterEl.querySelectorAll(
    `.js--mission-filter-county-clear`
  )

  addEventListener(countyClearButtons, `click`, (event, buttonEl) => {
    const countyEl = buttonEl.closest(`.js--mission-filter-county`)
    if (!countyEl) return

    const checkboxes = countyEl.querySelectorAll(`.js--mission-filter-location`)

    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.checked = false
        checkbox.dispatchEvent(new Event(`change`, { bubbles: true }))
      }
    })
    updateAllSummaries()
    updateLocationActiveStates()
    updateClearAllVisibility()
    updateClearAllLabelCount()
    updateOpenButtonBadge()
  })

  // County-level "Alla kommuner" (select all municipalities within a county).
  // Use event delegation on the document so the button keeps working even if
  // Turbo (or anything else) replaces the filter panel DOM after the initial
  // page load. Direct per-button listeners would be lost in that case because
  // turbo:load does not fire on frame-only updates.
  const onCountySelectAllClick = (event) => {
    const buttonEl = event.target.closest(
      `.js--mission-filter-county-select-all`
    )
    if (!buttonEl) return
    const countyEl = buttonEl.closest(`.js--mission-filter-county`)
    if (!countyEl || !filterEl.contains(countyEl)) return

    const checkboxes = countyEl.querySelectorAll(`.js--mission-filter-location`)
    const allChecked = Array.prototype.every.call(
      checkboxes,
      (cb) => cb.checked
    )

    // Toggle behavior: if all are checked, uncheck all; otherwise check all
    checkboxes.forEach((checkbox) => {
      const shouldBeChecked = !allChecked
      if (checkbox.checked !== shouldBeChecked) {
        checkbox.checked = shouldBeChecked
        checkbox.dispatchEvent(new Event(`change`, { bubbles: true }))
      }
    })

    updateAllSummaries()
    updateLocationActiveStates()
    updateClearAllVisibility()
    updateClearAllLabelCount()
    updateOpenButtonBadge()
  }
  document.addEventListener(`click`, onCountySelectAllClick)

  // County accordions (each county as its own accordion within Plats).
  // Use event delegation on the document so the toggles keep working even if
  // Turbo (or anything else) replaces the filter panel DOM after the initial
  // page load. Direct per-button listeners would be lost in that case because
  // turbo:load does not fire on frame-only updates.
  const onCountyToggleClick = (event) => {
    const buttonEl = event.target.closest(`.js--mission-filter-county-toggle`)
    if (!buttonEl) return
    const countyEl = buttonEl.closest(`.js--mission-filter-county`)
    if (!countyEl || !filterEl.contains(countyEl)) return

    // Don't toggle when clicking the per-county clear button nested in the header.
    if (event.target.closest(`.js--mission-filter-county-clear`)) return

    const bodyEl = countyEl.querySelector(
      `.mission-search-filter__county-locations`
    )
    if (!bodyEl) return

    const expanded = buttonEl.getAttribute(`aria-expanded`) === `true`
    buttonEl.setAttribute(`aria-expanded`, expanded ? `false` : `true`)
    bodyEl.hidden = expanded
    countyEl.classList.toggle(`is-open`, !expanded)
  }
  document.addEventListener(`click`, onCountyToggleClick)

  // Make entire municipality row clickable (not just the checkbox)
  const locationRows = filterEl.querySelectorAll(
    `.mission-search-filter__location`
  )

  addEventListener(locationRows, `click`, (event, rowEl) => {
    // Let the native checkbox click behave normally
    if (event.target && event.target.tagName === `INPUT`) return

    const checkbox = rowEl.querySelector(`.js--mission-filter-location`)
    if (!checkbox) return

    event.preventDefault()
    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event(`change`, { bubbles: true }))
    updateAllSummaries()
    updateLocationActiveStates()
    updateClearAllVisibility()
    updateClearAllLabelCount()
    updateOpenButtonBadge()
  })

  // Keep section summaries in sync when any input changes
  const allSectionInputs = filterEl.querySelectorAll(
    `.js--mission-filter-section input[type="checkbox"], .js--mission-filter-section input[type="radio"]`
  )

  addEventListener(allSectionInputs, `change`, () => {
    updateAllSummaries()
    updateLocationActiveStates()
    updateClearAllVisibility()
    updateClearAllLabelCount()
    updateOpenButtonBadge()
  })

  // Clear all filters without reloading
  const clearAllEls = document.querySelectorAll(
    `.mission-search-filter__clear-all, .mission-search-filter__clear-all-label, .js--mission-search-filter-clear`
  )

  addEventListener(clearAllEls, `click`, (event, element) => {
    event.preventDefault()
    event.stopPropagation()

    const formEl = filterEl.querySelector(`.mission-search-filter__body`)
    const inputs = (formEl || filterEl).querySelectorAll(
      `input, select, textarea`
    )

    inputs.forEach((input) => {
      if (input.type === `checkbox` || input.type === `radio`) {
        if (input.checked) {
          input.checked = false
          input.dispatchEvent(new Event(`change`, { bubbles: true }))
        }
      } else if (input.type === `hidden`) {
        // leave hidden inputs (CSRF tokens, etc.) untouched
      } else if (input.value !== ``) {
        input.value = ``
        input.dispatchEvent(new Event(`change`, { bubbles: true }))
      }
    })

    // Also clear the search title input in the outer search form
    const titleInput = document.querySelector(
      `.mission-search-page-form input[name="q[title]"]`
    )
    if (titleInput && titleInput.value !== ``) {
      titleInput.value = ``
    }

    updateAllSummaries()
    updateLocationActiveStates()
    updateClearAllVisibility()
    updateClearAllLabelCount()
    updateOpenButtonBadge()

    close()

    // The inner filter form has data-controller="mission-form" which auto-submits
    // on `change`/`input` events via Turbo, so dispatching change events on
    // cleared inputs above is enough to refresh the results frame.
    // As a safety net (e.g. if no inputs were checked), submit the inner form
    // explicitly via Turbo.
    const innerForm = document.querySelector(`.mission-search-filter__body`)
    if (innerForm) innerForm.requestSubmit()
  })

  // External hook: allow other scripts that programmatically toggle filter
  // inputs to request a full UI re-sync by dispatching a `missions-search:sync`
  // event. Dispatching native `change` events on individual inputs only covers
  // the inputs captured at
  // init time, while location checkboxes nested inside the county accordions
  // sit outside the `.js--mission-filter-section` selector and would
  // otherwise leave the section badges, the per-county active state and the
  // "Rensa alla filter" label out of sync.
  const onMissionsSearchSync = () => {
    updateAllSummaries()
    updateLocationActiveStates()
    updateClearAllVisibility()
    updateClearAllLabelCount()
    updateOpenButtonBadge()
  }
  document.addEventListener(`missions-search:sync`, onMissionsSearchSync)

  // Initial summaries on page load
  updateAllSummaries()
  updateLocationActiveStates()
  updateClearAllVisibility()
  updateClearAllLabelCount()
  updateOpenButtonBadge()

  return () => {
    document.removeEventListener(`click`, onDocumentClick)
    document.removeEventListener(`click`, onSectionToggleClick)
    document.removeEventListener(`click`, onCountyToggleClick)
    document.removeEventListener(`click`, onCountySelectAllClick)
    document.removeEventListener(`missions-search:sync`, onMissionsSearchSync)
    try {
      focusTrap.deactivate()
    } catch (e) {
      // ignore
    }
  }
})
